import { logger, monitoring } from 'common/services';
import { selectMyCommunityId } from 'community/api';

import { evaluate } from '../core';

import type { Action, PermissionDecision, PermissionsScope, ResourceFacts } from '../core';
import type { AppThunk } from 'store';

/**
 * The permission entry point for callers that are not components.
 *
 * One decision maker, two ways in: a rendered gate asks `useCan`, a Redux listener asks this. Both
 * end at the same pure `evaluate` over a snapshot from the same adapter, so the answer cannot
 * depend on which one asked (R6).
 *
 * **A thunk rather than a selector, and that is the whole design (KTD4).** A selector can read
 * `endpoint.select(arg)(state)` but cannot start the request. An uninitialized cache entry has no
 * data and no error, which reaches `evaluate` as a *settled denial* rather than as a pending one -
 * so the prompt would not hold, it would disappear. This dispatches `initiate`, awaits it, and only
 * then reads state.
 *
 * **What that claim is NOT.** An earlier version of this comment called it "the one permission
 * decision in the app with no component to subscribe the query". That is false, and the correction
 * matters more than the original claim did: `withUserRole` calls `usePrefetchPermissions`, and it
 * wraps the event-room route too (`Dashboard.tsx`), so at `eventEnded` the entry is normally
 * already warm. The round trip below is therefore usually a cache hit. It stays because the
 * alternative is a permission decision whose correctness depends on which route happens to be
 * mounted - and the whole point of an entry point is that it answers the same way from anywhere.
 *
 * Returns the full `PermissionDecision` rather than a boolean, so a caller can tell a determined
 * denial from an unanswerable one. Both deny; only one is worth reporting.
 *
 * @param facts what the caller already knows about the resource. Refines the answer per the
 * `FACT_RULES` table in `evaluate`; most actions ignore it.
 * @param scope which community or organisation to ask about. Defaults to the active community.
 */

/** Denied because the question could not be put, which is not the same as answered "no". */
const UNANSWERABLE: PermissionDecision = { allowed: false, isLoading: false, isError: true };

export const resolvePermission =
  (action: Action, facts?: ResourceFacts, scope?: PermissionsScope): AppThunk<Promise<PermissionDecision>> =>
  async (dispatch, getState) => {
    /**
     * Imported at call time, not at module load, and this is load-bearing rather than tidy.
     *
     * The only caller is a Redux listener, so this module is reachable from `store/features/event`'s
     * barrel - which exports its listeners before its selectors. Pulling the adapters in statically
     * put `community/api`, `organization/api`, `permissionsApi` and the two snapshot hooks into that
     * barrel's evaluation, and the cycle it closed left `selectActiveEventId` undefined at the
     * moment `pages/EventBroadcast/state/chat` built a selector from it. Forty-five test files
     * failed to collect, with an error naming neither this file nor permissions.
     *
     * Deferring the import breaks the cycle at the only edge that has to exist: nothing needs the
     * permission layer until something actually asks a question. `evaluate` stays static because
     * it is a leaf - pure logic, no store, no React.
     *
     * It defers less than the module graph suggests, and that is worth knowing rather than
     * implying otherwise: `selectServicePermissionsSnapshot` statically pulls `permissionsApi`,
     * `community/api`, `user/api` and `useServicePermissionsSnapshot` in with it, so by the time
     * the service branch below awaits `store/apis/permissionsApi` that module is already resolved.
     * Breaking the cycle is what this buys; a smaller initial bundle is not.
     */
    let adapters;

    try {
      adapters = await Promise.all([
        import('../adapters/service/selectServicePermissionsSnapshot'),
        import('../adapters/service/toServiceScope'),
      ]);
    } catch (error) {
      /**
       * A chunk that will not load is the one failure here that is not a permission answer at all.
       * Left to reject, it escapes into `createListenerMiddleware`'s default `onError` - a bare
       * `console.error` that reaches neither `logger` nor `monitoring`, and never reaches the
       * caller's own unanswerable branch. Reported here and returned as unanswerable so it lands
       * in the one place a caller already handles.
       */
      logger.error('[resolvePermission] Could not load the permission adapters', error);
      monitoring.reportError(error, { context: 'authorization.resolve_permission.chunk_load', action });

      return UNANSWERABLE;
    }

    const [{ selectServicePermissionsSnapshot }, { toServiceScope }] = adapters;

    const activeCommunityId = selectMyCommunityId(getState());

    /**
     * The scope actually asked about, resolved ONCE so the fetch and the read cannot disagree.
     *
     * `selectServicePermissionsSnapshot` defaults an absent scope to the active community, but the
     * fetch used to be guarded on `scope !== undefined` - so the only caller there is, which passes
     * no scope, fetched nothing and then read a cache entry keyed to the active community anyway.
     * The two halves of "fetch before read" were defaulting differently, which is precisely the
     * hole KTD4 exists to close, reopened on the branch that is not live yet.
     *
     * Resolving here also pins the community across the await: `getState()` after the round trip
     * may report a different active community than the one that was fetched.
     */
    const askedScope: PermissionsScope | undefined =
      scope ?? (activeCommunityId !== undefined ? { communityId: activeCommunityId } : undefined);

    /**
     * `subscribe: false` on both, matching `CreateIntakeContainer.tsx`. The request still runs and
     * the promise still settles with it, so fetch-before-read is unchanged - but the cache entry
     * stays evictable. A subscribing `initiate` is never unsubscribed here, and RTK Query's GC only
     * runs once a key has no subscribers left, so every event end would pin one more forever.
     */
    if (askedScope !== undefined) {
      const { permissionsApi } = await import('store/apis/permissionsApi');

      await dispatch(
        permissionsApi.endpoints.getMyPermissions.initiate(toServiceScope(askedScope), { subscribe: false })
      );
    }

    // Read AFTER the await, never before: the point of the round trip is that the state this
    // resolves against is the one the request produced.
    const state = getState();
    const snapshot = selectServicePermissionsSnapshot(state, askedScope);

    return evaluate(snapshot, action, facts);
  };
